import {
  fetchRepositoryFromServer,
  fetchTopRepositories,
  RepositoryPicker,
  type RepositoryPickerProps,
  type RepositoryPickerRepository,
  repositoryQueryKey,
} from '@github-ui/issues-repository-picker'
import {useItemPickersContext} from '@github-ui/item-picker-base/ItemPickersContext'
import {useQuery} from '@github-ui/react-query'
import {useSyncedState} from '@github-ui/use-synced-state'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {IconButton, type TokenProps} from '@primer/react'
import type React from 'react'
import {useCallback, useEffect, useEffectEvent, useState} from 'react'

import {IssuePicker, type IssuePickerItem, type IssuePickerProps} from './IssuePicker'
import styles from './RepositoryAndIssuePicker.module.css'

export type RepositoryAndIssuePickerProps = Omit<TokenProps, 'leadingVisual' | 'text' | 'ref'> & {
  anchorElement: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  onIssueSelection: (issues: IssuePickerItem[]) => void
  organization: string
  defaultRepositoryNameWithOwner: string
  selectedIssueIds?: string[]
  hiddenIssueIds?: string[]
  // pickerType allows this to be treated as a controlled component
  pickerType?: PickerType
  onPickerTypeChange?: (pickerType: PickerType) => void
  withParent?: boolean
} & {
  issuePickerProps?: Partial<Omit<IssuePickerProps, 'repositoryNameWithOwner'>>
  repositoryPickerProps?: Partial<RepositoryPickerProps>
}

export type PickerType = 'Repository' | 'Issue' | null

const REPOSITORY_PICKER_ID = 'repository-picker'
const ISSUE_PICKER_ID = 'issue-picker'

type RepositoryAndIssuePickerInteralProps = {
  pickerType: Exclude<PickerType, null>
  setPickerType: React.Dispatch<React.SetStateAction<PickerType>>
} & Omit<RepositoryAndIssuePickerProps, 'pickerType'>

type RepositoryAndIssuePickerInternalCurrentRepositoryProps = Omit<
  RepositoryAndIssuePickerInteralProps,
  'defaultRepositoryNameWithOwner'
> & {
  currentRepositoryNameWithOwner: string
  setCurrentRepositoryNameWithOwner: React.Dispatch<React.SetStateAction<string>>
}

type RepositoryAndIssuePickerInternalDataProps = RepositoryAndIssuePickerInternalCurrentRepositoryProps & {
  initialRepository: RepositoryPickerRepository | undefined
}

export function RepositoryAndIssuePicker({
  pickerType: propsPickerType = null,
  anchorElement,
  onPickerTypeChange,
  selectedIssueIds,
  hiddenIssueIds,
  withParent,
  ...rest
}: RepositoryAndIssuePickerProps) {
  const [pickerType, setPickerType] = useSyncedState<PickerType>(propsPickerType)

  const notifyPickerTypeChange = useEffectEvent((type: PickerType) => {
    // don't trigger if the internal pickertype is the same as the external
    if (propsPickerType === type) return
    onPickerTypeChange?.(type)
  })

  useEffect(() => {
    notifyPickerTypeChange(pickerType)
  }, [pickerType])

  if (pickerType === null) {
    return anchorElement({onClick: () => setPickerType('Issue')})
  }

  return (
    <RepositoryAndIssuePickerInternal
      anchorElement={anchorElement}
      selectedIssueIds={selectedIssueIds}
      hiddenIssueIds={hiddenIssueIds}
      withParent={withParent}
      {...rest}
      setPickerType={setPickerType}
      pickerType={pickerType}
    />
  )
}

function RepositoryAndIssuePickerInternal({
  defaultRepositoryNameWithOwner,
  ...props
}: RepositoryAndIssuePickerInteralProps) {
  const [currentRepositoryNameWithOwner, setCurrentRepositoryNameWithOwner] =
    useState<string>(defaultRepositoryNameWithOwner)

  return (
    <RepositoryAndIssuePickerInternalCurrentRepository
      {...props}
      currentRepositoryNameWithOwner={currentRepositoryNameWithOwner}
      setCurrentRepositoryNameWithOwner={setCurrentRepositoryNameWithOwner}
    />
  )
}

function RepositoryAndIssuePickerInternalCurrentRepository({
  currentRepositoryNameWithOwner,
  ...props
}: RepositoryAndIssuePickerInternalCurrentRepositoryProps) {
  const [owner, name] = currentRepositoryNameWithOwner.split('/')
  const query = useQuery({
    queryKey: repositoryQueryKey(owner ?? '', name ?? ''),
    queryFn: ({signal}) => fetchRepositoryFromServer(owner ?? '', name ?? '', signal),
    enabled: Boolean(owner && name),
  })

  if (query.isPending) {
    return (
      <RepositoryAndIssuePickerInternalData
        {...props}
        currentRepositoryNameWithOwner={currentRepositoryNameWithOwner}
        initialRepository={undefined}
      />
    )
  }
  // If the fetch failed, we still render the picker — just without a pre-selected repository. Some
  // places that use this picker (issue transfer, sub-issue/dependency pickers, the memex parent
  // editor, code-scanning tracking) don't wrap it in an error boundary, so throwing here would take
  // the whole page down. query.data is null when the repo isn't accessible, so `?? undefined` means
  // "nothing pre-selected".
  return (
    <RepositoryAndIssuePickerInternalData
      {...props}
      currentRepositoryNameWithOwner={currentRepositoryNameWithOwner}
      initialRepository={query.data ?? undefined}
    />
  )
}

function RepositoryAndIssuePickerInternalData({
  anchorElement,
  currentRepositoryNameWithOwner,
  hiddenIssueIds,
  initialRepository,
  issuePickerProps,
  onIssueSelection,
  organization,
  pickerType,
  repositoryPickerProps,
  selectedIssueIds,
  setCurrentRepositoryNameWithOwner,
  setPickerType,
  withParent,
}: RepositoryAndIssuePickerInternalDataProps) {
  const [topRepositories, setTopRepositories] = useState<RepositoryPickerRepository[] | null>(null)
  const {updateOpenState} = useItemPickersContext()

  const onBackClick = useCallback(() => {
    setPickerType('Repository')
  }, [setPickerType])

  const onRepoClose = useCallback(() => {
    // If the picker type is Issue, then we know this component has moved forward to the issue picking stage, so we
    // shouldn't update the picker type to overwrite that
    setPickerType(t => (t === 'Issue' ? 'Issue' : null))
    updateOpenState(REPOSITORY_PICKER_ID, false)
  }, [setPickerType, updateOpenState])

  const onIssueClose = useCallback(() => {
    // If the picker type is Repository, then we know this has changed due to switching back to the repository
    // picker, so we shouldn't update the picker type to overwrite that
    setPickerType(t => (t === 'Repository' ? 'Repository' : null))
    updateOpenState(ISSUE_PICKER_ID, false)
  }, [setPickerType, updateOpenState])

  const setRepository = useCallback(
    (repository: RepositoryPickerRepository | undefined) => {
      if (repository?.nameWithOwner) {
        setCurrentRepositoryNameWithOwner(repository.nameWithOwner)
      }
      // if there was no newly selected repository/NWO, that means that the already selected repository was "de-selected",
      // but with this dual-picker setup, "deselecting" still means "continue to the next picker with the selected repository"
      if (currentRepositoryNameWithOwner || repository?.nameWithOwner) {
        setPickerType('Issue')
      }
    },
    [currentRepositoryNameWithOwner, setCurrentRepositoryNameWithOwner, setPickerType],
  )

  const localOnIssueSelection = (issues: IssuePickerItem[]) => {
    onIssueSelection(issues)
    setPickerType(null)
  }

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const repos = await fetchTopRepositories({
        first: 10,
        hasIssuesEnabled: true,
        owner: organization,
      })
      if (!cancelled) setTopRepositories(repos)
    })()

    return () => {
      cancelled = true
    }
  }, [organization])

  switch (pickerType) {
    case 'Repository':
      if (!topRepositories) return null

      return (
        <RepositoryPicker
          initialRepository={initialRepository}
          preventDefault
          onSelect={setRepository}
          organization={organization}
          topRepositories={topRepositories}
          anchorElement={anchorElement}
          onClose={onRepoClose}
          triggerOpen
          title="Select a repository"
          pickerId={REPOSITORY_PICKER_ID}
          {...repositoryPickerProps}
        />
      )
    case 'Issue':
      return (
        <IssuePicker
          selectedIssueIds={selectedIssueIds}
          hiddenIssueIds={hiddenIssueIds}
          onIssueSelection={localOnIssueSelection}
          anchorElement={anchorElement}
          repositoryNameWithOwner={currentRepositoryNameWithOwner}
          onClose={onIssueClose}
          withParent={withParent}
          title={
            <div className={styles.BackToRepositorySelectionButton}>
              <IconButton
                icon={ArrowLeftIcon}
                variant="invisible"
                data-testid="back-to-repo-selection-button"
                aria-label="Back to repository selection"
                tooltipDirection="n"
                onClick={onBackClick}
              />
              <span>{currentRepositoryNameWithOwner}</span>
            </div>
          }
          triggerOpen
          pickerId={ISSUE_PICKER_ID}
          {...issuePickerProps}
        />
      )
  }
}
